import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";
import { RED, GREEN } from "assets/theme/brandColors";
import { indefiniteArticle } from "utils/grammar";

const ACHIEVEMENT_MAP = {
  "eggs cracked": { icon: "egg", color: "#f59e0b" },
  "onions cut": { icon: "restaurant_menu", color: GREEN },
  "minutes spent cooking": { icon: "schedule", color: "#1976d2" },
  "meals made": { icon: "restaurant", color: RED },
  "schedules completed": { icon: "event_available", color: "#7c3aed" },
};

function OrdersOverview({
  justLevelUp,
  justDistinctionUp,
  computedLevel,
  distinction,
  nextDistinction,
}) {
  const [achievements, setAchievements] = useState([]);
  const [levelUp, setLevelUp] = useState(false);
  const [distinctionMessage, setDistinctionMessage] = useState("");
  const [nextLevel, setNextLevel] = useState(null);
  const [nextName, setNextName] = useState("");

  useEffect(() => {
    async function fetchAchievements() {
      try {
        const achRes = await apiClient.get(ENDPOINTS.users.achievements);
        setAchievements(
          (achRes.data.achievements || []).map((a) => ({
            count: a.count,
            label: a.display_name,
            ...(ACHIEVEMENT_MAP[a.display_name.toLowerCase()] || { icon: "star", color: "#999" }),
          }))
        );
      } catch (err) {
        console.error("Error fetching achievements:", err.message);
      }
    }

    fetchAchievements();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchAchievements();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // Show level-up dialog when parent passes updated flags
  useEffect(() => {
    if (justLevelUp || justDistinctionUp) {
      setLevelUp(true);
      if (justDistinctionUp && nextDistinction) {
        const [nextLvl, , nextDist] = nextDistinction;
        setDistinctionMessage(distinction ?? "");
        setNextLevel(nextLvl);
        setNextName(nextDist);
      }
    }
  }, [justLevelUp, justDistinctionUp, distinction, nextDistinction]);

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            Lifetime
          </MDTypography>

          {achievements.length === 0 ? (
            <MDTypography variant="body2" color="secondary" textAlign="center" py={2}>
              Stats appear after your first recipe 🍽️
            </MDTypography>
          ) : (
            achievements.map(({ count, label, icon, color }, index) => (
              <MDBox
                key={label}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={1.25}
                sx={{
                  borderBottom: index < achievements.length - 1 ? "1px solid #f5f5f5" : "none",
                }}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ fontSize: 18, color }}>{icon}</Icon>
                  <MDTypography
                    variant="button"
                    color="text"
                    sx={{ fontSize: "0.875rem", fontWeight: 400 }}
                  >
                    {label}
                  </MDTypography>
                </MDBox>
                <MDTypography variant="button" fontWeight="bold" color="text">
                  {count}
                </MDTypography>
              </MDBox>
            ))
          )}
        </MDBox>
      </Card>

      {/* Level-up dialog */}
      <Dialog open={levelUp} onClose={() => setLevelUp(false)} maxWidth="xs" fullWidth>
        <DialogTitle textAlign="center">🎉 Level Up! 🎉</DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="h5" gutterBottom>
            Congrats! You&apos;ve reached Level {computedLevel}!
          </Typography>

          {distinctionMessage && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              You are now a{" "}
              <Typography component="span" sx={{ fontWeight: "bold", color: "#4caf50" }}>
                {distinctionMessage}
              </Typography>
              . Get to level{" "}
              <Typography component="span" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                {nextLevel}
              </Typography>{" "}
              to become {indefiniteArticle(nextName)}{" "}
              <Typography component="span" sx={{ fontWeight: "bold", color: "#F57C00" }}>
                {nextName}
              </Typography>
              .
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={() => {
              setLevelUp(false);
              if (distinctionMessage) window.location.reload();
            }}
            sx={{ mt: 3, bgcolor: RED, color: "#fff", "&:hover": { bgcolor: "#7d1520" } }}
          >
            Awesome!
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

OrdersOverview.propTypes = {
  justLevelUp: PropTypes.bool,
  justDistinctionUp: PropTypes.bool,
  computedLevel: PropTypes.number,
  distinction: PropTypes.string,
  nextDistinction: PropTypes.array,
};

OrdersOverview.defaultProps = {
  justLevelUp: false,
  justDistinctionUp: false,
  computedLevel: null,
  distinction: null,
  nextDistinction: null,
};

export default OrdersOverview;
